/**
 * Data hooks for invoice creation and management.
 *
 * Scoped to the active business selected by the user.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import {
  invoiceFormOptions,
  issueSalesInvoice,
  updateSalesInvoiceDraft,
  type InvoiceFormOptions,
  type IssueSalesInvoiceInput,
  type UpdateSalesInvoiceDraftInput,
} from '@data/services/invoiceService';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { loadDocument } from '@data/services/documentReader';
import { DOCUMENTS_QUERY_ROOT, useActiveBusiness, useDocument } from '@features/documents/useDocuments';

export const INVOICE_OPTIONS_QUERY_ROOT = ['invoice-form-options'] as const;

/**
 * Options (parties and products) for building an invoice in the active business.
 */
export function useInvoiceOptions() {
  const business = useActiveBusiness();
  const businessId = business.data ?? null;

  return useQuery({
    queryKey: [...INVOICE_OPTIONS_QUERY_ROOT, businessId],
    queryFn: async (): Promise<InvoiceFormOptions> => {
      if (businessId === null) {
        return { customers: [], products: [] };
      }
      return invoiceFormOptions(getDb() as never, businessId);
    },
    enabled: businessId !== null,
  });
}

/**
 * Active business record, needed to calculate tax splits (INTRA vs INTER).
 */
export function useCurrentBusinessProfile() {
  const business = useActiveBusiness();
  const businessId = business.data ?? null;

  return useQuery({
    queryKey: ['business-profile-current', businessId],
    queryFn: async () => {
      if (businessId === null) return null;
      return resolveActiveBusiness(getDb() as never);
    },
    enabled: businessId !== null,
  });
}

/**
 * Load one invoice into the editor, when editing a DRAFT.
 *
 * Returns null for anything that is not an editable draft, and the SCREEN
 * decides what to do about it. The rule "only a draft is editable" lives here
 * so the route, the screen and the write path cannot each form a different
 * opinion: `updateSalesInvoiceDraft` refuses an issued document anyway, and this
 * keeps the user from being offered an edit that would be rejected on save.
 *
 * Issued and cancelled invoices come back as null deliberately, so an issued
 * invoice reached through the edit route renders the ordinary document screen
 * rather than an editor full of controls that cannot be used.
 */
export function useEditableInvoice(documentId: string | null) {
  const query = useDocument(documentId);
  // The business state is REQUIRED to normalise a document, and it decides the
  // tax split: an inter-state invoice shows IGST and an intra-state one shows
  // CGST+SGST. Passing '' here made every reopened draft normalise as
  // intra-state, so an IGST invoice opened for editing displayed the WRONG tax
  // legs - and, because `formFromDraft` hydrates the form from this model, the
  // figure the user was shown before saving was not the figure they got.
  //
  // Read from the active business, which `useCurrentBusinessProfile` already
  // resolves. Disabled until it lands, so the editor never briefly renders an
  // empty form built from a mis-split document.
  const profile = useCurrentBusinessProfile();

  return useQuery({
    queryKey: ['invoice-editable', documentId, profile.data?.stateCode ?? null],
    queryFn: async () => {
      if (!documentId) return null;
      const model = await loadDocument(getDb() as never, documentId, {
        businessStateCode: profile.data?.stateCode ?? '',
      });
      // Only a DRAFT is editable. Anything else is a historical record.
      return model.status === 'DRAFT' ? model : null;
    },
    enabled: documentId !== null && profile.data != null,
    // `useDocument` already holds the document; reuse it rather than loading
    // the same rows twice for the same id. Guarded on the profile having landed,
    // or `initialData` would short-circuit the very query that fixes the split.
    initialData:
      profile.data != null && query.data?.status === 'DRAFT' ? query.data : undefined,
  });
}

/**
 * Mutation hook to edit an existing DRAFT invoice.
 *
 * Separate from `useCreateInvoice` on purpose. They write different things -
 * one INSERTs, one REPLACES a draft's contents - and merging them behind a
 * single "save" flag would put an `id` on a code path that has no business
 * carrying one.
 *
 * Invalidates the document list AND the open document, because both views are
 * now stale: the list shows the new total, and the document screen shows the
 * preview the user is about to issue.
 */
export function useUpdateInvoiceDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSalesInvoiceDraftInput) => {
      // No businessId, and none needed. `UpdateSalesInvoiceDraftInput` has no
      // such field on purpose — an edit may not redirect a document to another
      // company, so the service resolves the real owner from the stored row.
      // Adding it back here would re-open the door the type was built to close.
      return updateSalesInvoiceDraft(getDb() as never, input);
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT });
      await queryClient.invalidateQueries({ queryKey: ['document', result.id] });
      await queryClient.invalidateQueries({ queryKey: ['invoice-editable', result.id] });
    },
  });
}

/**
 * Mutation hook to CREATE a sales invoice.
 *
 * Invalidates the document list so the newly created draft appears immediately.
 * Note WHAT this writes: a DRAFT, not an issued invoice. The screen says "Save
 * draft" for exactly that reason — the old button said "Create invoice", which
 * told the user they had an invoice when they held something still editable.
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: Omit<IssueSalesInvoiceInput, 'businessId'> & { businessId?: string },
    ) => {
      let bId = input.businessId;
      if (!bId) {
        const active = await resolveActiveBusiness(getDb() as never);
        bId = active.id;
      }
      return issueSalesInvoice(getDb() as never, {
        ...input,
        businessId: bId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT });
    },
  });
}
